// MF-568 — Helpers shared by the PAT progress cards (paired scores, class panels, flows).
import { useEffect, useRef } from 'react'
import { benchmarkColor } from '../boardPalettes'

export const STANINE_NOTE = "Stanines need the school's norms (Board Builder → PAT settings)"

/** '115-124' → 115 (the lower bound orders bands); anything else → null. */
export const bandLow = (band) => {
  const m = /^\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*$/.exec(String(band ?? ''))
  return m ? Number(m[1]) : null
}
export const bandHigh = (band) => {
  const m = /-\s*(\d+(?:\.\d+)?)\s*$/.exec(String(band ?? ''))
  return m ? Number(m[1]) : null
}
export const byBand = (a, b) => (bandLow(a) ?? 0) - (bandLow(b) ?? 0) || String(a).localeCompare(String(b))

export const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v))
export const fmtStanine = (v) => (isNum(v) ? String(Math.round(Number(v))) : 'N/A')
export const fmtChange = (d) =>
  !isNum(d) ? '–' : `${Number(d) > 0 ? '+' : ''}${Math.round(Number(d) * 10) / 10}`

export const fmtDate = (iso) => {
  if (!iso) return '–'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Whole months between two dates, or null. */
export const monthsBetween = (from, to) => {
  const a = new Date(from)
  const b = new Date(to)
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null
  return Math.round((b - a) / (30.4375 * 86400000))
}

/** Text for the gap between two results when it is not about a year ('' otherwise). */
export const elapsedText = (from, to) => {
  const m = monthsBetween(from, to)
  if (m === null || (m >= 11 && m <= 13)) return ''
  return m < 1 ? 'under 1 month' : `${m} month${m === 1 ? '' : 's'}`
}

/** Mix a #rrggbb colour with white (t = share of white) for a light cell tint. */
export const tint = (hex, t = 0.72) => {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16))
  return `rgb(${c.map((v) => Math.round(v + (255 - v) * t)).join(',')})`
}
/** Colour of a value inside [min, max] on the Maestro orange→blue benchmark scale. */
export const rangeColor = (v, min, max) =>
  benchmarkColor(max > min ? ((Number(v) - min) / (max - min)) * 100 : 50)

/** rows (one per student) → { matched: with a previous result, single: only one result } */
export const splitPairs = (rows = []) => {
  const matched = []
  const single = []
  for (const r of rows) {
    if (!isNum(r.score)) continue
    if (isNum(r.prev_score)) matched.push(r)
    else single.push(r)
  }
  return { matched, single }
}

export const changeOf = (r) => (isNum(r.change) ? Number(r.change) : Number(r.score) - Number(r.prev_score))

/** Follow the tile size with an ECharts instance (ECharts measures its box once). */
export const useChartResize = () => {
  const chartRef = useRef(null)
  const boxRef = useRef(null)
  useEffect(() => {
    const el = boxRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(() => chartRef.current?.getEchartsInstance?.()?.resize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return { chartRef, boxRef }
}
